import { Component, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TierService } from '../../services/tier.service';
import { SystemAccessService } from '../../services/system-access.service';
import { MembershipService } from '../../services/membership.service';
import { L10nPluralPipe } from '../../pipes/l10n-plural.pipe';

type TrialSeverity = 'info' | 'warning' | 'urgent' | 'lastDay';

/**
 * The banner of a trial that is still running. It carries up to two lines:
 *  - the COUNTDOWN, in the trial's last ten days, escalating tone as the end approaches
 *    (info → warning → urgent → lastDay) so the brand has time to choose a plan before edits pause;
 *    every sentence that names a number of days is a plural family, so one day reads "1 day"
 *    and each language declines the noun by the count;
 *  - the line about IMAGES STILL WAITING TO BE TAGGED: a trial has a bounded number of images tagged
 *    by AI and the rest wait for a plan, so the line shows for as long as the trial runs and images
 *    wait — at any distance from the trial's end, which is why the banner can show without a countdown.
 * Reads TierService signals, so it updates live when the plan state refreshes. Self-hides outside a
 * running trial: an ended trial has its own banner (the read-only one), and status is null on public routes.
 *
 * The door to the plans (the Plan & usage settings tab, /admin/settings/plan-usage) shows to a member who may
 * manage billing, and only that member reads the lines that ask for a plan. Everyone else reads the trial's PLAIN
 * lines and no door: when the trial ends ("Your trial ends in 3 days." / "Your trial ends today.") and that the rest
 * of the images get tagged once the brand picks a plan. Picking a plan is a billing act and that page refuses everyone
 * else, so a door a member cannot walk through, or an instruction they cannot follow, is noise at the top of every
 * page, and an ask-an-admin sentence there asks something of a member the trial asks nothing of (Robert's rulings of
 * 2026-10-06; the plan-ended banner keeps its ask, because there the brand is read-only and the sentence names the way
 * out). The calm sentence of the first days carries no instruction and reads the same for both. Until the permission
 * answers, the banner reads as for a member without the right, so an instruction never shows without its door. The
 * colour still follows the days left, for every reader.
 * Cloned from demo-banner.
 */
@Component({
  selector: 'app-trial-banner',
  standalone: true,
  imports: [TranslateModule, RouterLink, L10nPluralPipe],
  templateUrl: './trial-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './trial-banner.component.scss'
})
export class TrialBannerComponent {
  protected readonly tier = inject(TierService);
  protected readonly canManageBilling = signal(false);
  private readonly access = inject(SystemAccessService);
  private readonly membership = inject(MembershipService);
  private permissionResolved = false;

  constructor() {
    // Admitted members (and roots) only: this banner mounts at the app root on EVERY route, including
    // anonymous portal/share loads and a public brand's signed-in visitors with no membership, and a
    // non-member's access query would be refused and latch error state in the shared permission service.
    // The probe hangs on ADMISSION, never on the bare sign-in; a monitoring root has no membership row and
    // keeps the probe through the root axis. Brand switches are full app boots (per-brand subdomain), so a
    // one-time resolve after admission holds. The plan-ended banner asks the same question the same way.
    // Planned: both app-root banners mount the shared plan door (`<app-plan-door>`) for this arm once that
    // primitive carries a tone for a coloured bar — its gain here: the door reads the live permission set, so a
    // billing right granted or revoked mid-session reaches the banner, where this one-time answer stays until reload.
    effect(() => {
      if (!this.membership.isAdmittedOrRoot() || this.permissionResolved) {
        return;
      }
      this.permissionResolved = true;
      untracked(() => {
        this.access.hasPermission('MANAGE_BILLING').then(v => this.canManageBilling.set(v));
      });
    });
  }

  /** The countdown: only while on trial with fewer than 10 days left. trialDaysRemaining() is null off-trial. */
  protected readonly showsCountdown = computed(() => {
    const status = this.tier.status();
    const days = this.tier.trialDaysRemaining();
    return status === 'TRIAL' && days !== null && days < 10;
  });

  /** The waiting-images line: while the trial runs and at least one image waits. */
  protected readonly showsDeferredImages = computed(() => {
    const status = this.tier.status();
    const waiting = this.tier.deferredImageCount();
    return status === 'TRIAL' && waiting > 0;
  });

  /** The banner shows when either line has something to say. Both are read before they are combined,
   *  so the computed tracks the two of them whichever is true first. */
  protected readonly shouldShow = computed(() => {
    const countdown = this.showsCountdown();
    const deferredImages = this.showsDeferredImages();
    return countdown || deferredImages;
  });

  /** Escalating tone by days remaining. null → info (defensive; a running trial always has its days). */
  protected readonly severity = computed<TrialSeverity>(() => {
    const days = this.tier.trialDaysRemaining();
    if (days === null) return 'info';
    if (days === 0) return 'lastDay';
    if (days < 3) return 'urgent';
    if (days < 5) return 'warning';
    return 'info';
  });
}
